import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import UserDashboardLayout from "../../layouts/UserDashboardLayout";
import {
  FiCalendar, FiCheckCircle, FiClock, FiTrendingUp,
  FiFeather, FiAward, FiChevronLeft, FiChevronRight,
  FiSunrise, FiWind, FiMoon, FiCheck, FiBell, FiActivity
} from "react-icons/fi";
import Topbar from "../../components/userDashboard/Topbar";
import BrandLoader from "../../components/BrandLoader";
import { useCheckIn } from "../../context/CheckInContext";
import { userAPI, liveSessionAPI, dietPlanAPI } from "../../services/api";
import { toast } from "react-toastify";

// ─── TOKENS & STYLES ─────────────────────────────────────────────────────────
const T = {
  orange: "#F29A0C", orange2: "#E97818",
  olive: "#6B8A5A",  olive2: "#8BA47A",
  pinkLight: "#FBCFE8", pinkLight2: "#F472B6",
  slateGray: "#9CA3AF",
  lightGray: "#E5E7EB",
  roseGold: "#D97706",  roseGold2: "#E85D88",
  dark: "#1F1F1F",     mid: "#555",
  muted: "#999",       light: "#CCC",
  border: "rgba(239,229,216,0.8)",
};

const glass = {
  background: "rgba(255,255,255,0.85)",
  border: "1px solid rgba(255,255,255,0.95)",
  backdropFilter: "blur(32px)",
  WebkitBackdropFilter: "blur(32px)",
  boxShadow: "0 20px 60px rgba(0,0,0,0.06), inset 0 1px 0 #fff",
};

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const DAYS_SHORT = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

const CAT_COLORS = {
  Asana: T.orange, Pranayama: T.olive, Meditation: "#E85D88",
  Breathwork: "#0F6E56", YTT: "#3E5C2E",
};
const CAT_BG = {
  Asana: "rgba(242,154,12,0.1)", Pranayama: "rgba(107,138,90,0.1)",
  Meditation: "rgba(232,93,136,0.1)", Breathwork: "rgba(15,110,86,0.1)", YTT: "rgba(62,92,46,0.1)",
};
const CAT_ICONS = {
  Asana: FiSunrise, Pranayama: FiWind, Meditation: FiMoon,
  Breathwork: FiWind, YTT: FiAward,
};

const DIET_TASKS_BY_DAY = {
  0: "Sunday Detox: Warm Lemon Water & Light Herbal Broth",
  1: "Monday Energy Boost: Green Smoothie & Roasted Sprouted Bowl",
  2: "Tuesday Balance: Chia Pudding & Steamed Vegetables with Quinoa",
  3: "Wednesday Vitality: Oats with Almond Butter & Lentil Soup",
  4: "Thursday Strength: Avocado Salad & High-Protein Dal",
  5: "Friday Rejuvenation: Coconut Water & Mixed Fruit Salad",
  6: "Saturday Recovery: Herbal Tea & Vegetable Kitchari"
};

const getLocalDateKey = (d) => {
  if (!d) return "";
  const dateObj = new Date(d);
  if (isNaN(dateObj.getTime())) return "";
  const yyyy = dateObj.getFullYear();
  const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
  const dd = String(dateObj.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// ─── MONTHLY CALENDAR COMPONENT ───────────────────────────────────────────────
function MonthCalendar({
  year,
  month,
  selectedDay,
  onSelect,
  onPrev,
  onNext,
  activityByDate = {},
  onHoverDay
}) {
  const firstDay = new Date(year, month, 1).getDay();
  const totalDays = new Date(year, month + 1, 0).getDate();
  const today = useMemo(() => new Date(), []);
  const todayKey = getLocalDateKey(today);

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(d);

  return (
    <div className="rounded-[32px] p-7 relative" style={glass}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-black text-[20px] text-[#1F1F1F]" style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-0.5px" }}>
            Monthly Calendar
          </h2>
          <p className="text-[12px] text-[#999] mt-0.5">{MONTHS[month]} {year}</p>
        </div>
        <div className="flex gap-2">
          {[FiChevronLeft, FiChevronRight].map((Icon, i) => (
            <motion.button
              key={i}
              whileHover={{ scale: 1.06 }}
              whileTap={{ scale: 0.92 }}
              onClick={i === 0 ? onPrev : onNext}
              className="w-9 h-9 rounded-full flex items-center justify-center cursor-pointer border border-[#EFE5D8] bg-white/90 shadow-sm"
            >
              <Icon size={15} className="text-[#666]" />
            </motion.button>
          ))}
        </div>
      </div>

      {/* Day Headers */}
      <div className="grid grid-cols-7 gap-1.5 mb-2">
        {DAYS_SHORT.map(d => (
          <div key={d} className="text-center text-[10px] font-bold uppercase tracking-[1px] py-1 text-[#AAA]">
            {d}
          </div>
        ))}
      </div>

      {/* Grid Cells */}
      <div className="grid grid-cols-7 gap-1.5">
        {cells.map((d, i) => {
          if (d === null) return <div key={i} className="aspect-square" />;
          const cellDate = new Date(year, month, d);
          const cellKey = getLocalDateKey(cellDate);
          const isToday = cellKey === todayKey;
          const isFuture = cellDate > today;
          const act = activityByDate[cellKey] || { liveCount: 0, videoCount: 0, checkedIn: false, missedCount: 0, items: [] };
          const isSelected = selectedDay === d;

          // Color calculation logic
          let bgStyle = "rgba(239,229,216,0.35)";
          let textColor = "#888";
          let shadow = "none";

          if (isToday) {
            bgStyle = `linear-gradient(135deg,${T.orange},${T.orange2})`;
            textColor = "#fff";
            shadow = `0 6px 18px rgba(242,154,12,0.45)`;
          } else if (isFuture) {
            bgStyle = "rgba(239,229,216,0.2)";
            textColor = "#CCC";
          } else if (act.liveCount >= 2) {
            // Elegant Rose Gold for 2+ Live Sessions
            bgStyle = `linear-gradient(135deg,${T.roseGold},${T.roseGold2})`;
            textColor = "#fff";
            shadow = `0 4px 14px rgba(217,119,6,0.38)`;
          } else if (act.liveCount === 1) {
            // Single Live Session Attended (Olive)
            bgStyle = `linear-gradient(135deg,${T.olive},${T.olive2})`;
            textColor = "#fff";
            shadow = `0 4px 14px rgba(107,138,90,0.35)`;
          } else if (act.videoCount > 0 || act.checkedIn) {
            // Practice Video watched or Daily Check-in (Orange)
            bgStyle = `linear-gradient(135deg,${T.orange},${T.orange2})`;
            textColor = "#fff";
            shadow = `0 4px 14px rgba(242,154,12,0.38)`;
          } else if (act.missedCount > 0) {
            // Missed session (Light Red)
            bgStyle = "rgba(226,75,74,0.12)";
            textColor = "#E24B4A";
          }

          return (
            <motion.div
              key={d}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: d * 0.008, type: "spring", stiffness: 280 }}
              onClick={() => !isFuture && onSelect(d)}
              onMouseEnter={() => onHoverDay && onHoverDay(d, cellDate, act)}
              onMouseLeave={() => onHoverDay && onHoverDay(null, null, null)}
              whileHover={!isFuture ? { scale: 1.15, zIndex: 10 } : {}}
              className="aspect-square rounded-[14px] flex flex-col items-center justify-center text-[12px] font-bold relative transition-all duration-200 cursor-pointer select-none"
              style={{
                background: bgStyle,
                color: textColor,
                boxShadow: shadow,
                outline: isSelected ? `2.5px solid ${T.orange}` : "none",
                outlineOffset: isSelected ? 2 : 0,
              }}
            >
              <span className="text-[12px] font-extrabold">{d}</span>
              {(act.liveCount > 0 || act.videoCount > 0 || act.checkedIn) && !isFuture && !isToday && (
                <div className="absolute bottom-1 w-1.5 h-1.5 rounded-full bg-white/80" />
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex gap-4 mt-6 pt-5 flex-wrap border-t border-[#EFE5D8]/60">
        {[
          [`linear-gradient(135deg,${T.roseGold},${T.roseGold2})`, "2+ Live Sessions"],
          [`linear-gradient(135deg,${T.olive},${T.olive2})`, "1 Live Session"],
          [`linear-gradient(135deg,${T.orange},${T.orange2})`, "Practice Video / Check-in"],
          ["rgba(226,75,74,0.15)", "Missed Session"],
          ["rgba(239,229,216,0.35)", "Rest / Rest Day"],
        ].map(([bg, label]) => (
          <div key={label} className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-[4px]" style={{ background: bg }} />
            <span className="text-[11px] font-semibold text-[#888]">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── YEAR-IN-REVIEW HEATMAP (Multi-Activity Color Scale) ──────────────────────
function YearHeatmap({ year, activityByDate = {}, dietCompletedState = {}, onHoverDay }) {
  const today = useMemo(() => new Date(), []);
  const todayKey = getLocalDateKey(today);

  const monthCols = useMemo(() => {
    return Array.from({ length: 12 }, (_, mIdx) => {
      const daysInMonth = new Date(year, mIdx + 1, 0).getDate();
      return {
        monthIndex: mIdx,
        monthName: MONTHS_SHORT[mIdx],
        days: Array.from({ length: daysInMonth }, (_, dIdx) => dIdx + 1)
      };
    });
  }, [year]);

  return (
    <div className="rounded-[32px] p-7 mb-6" style={glass}>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div>
          <h2 className="font-black text-[19px] text-[#1F1F1F]" style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-0.5px" }}>
            Year-in-Review Overall Attendance & Activity ({year})
          </h2>
          <p className="text-[12px] text-[#999] mt-0.5">Comprehensive score combining check-in, live classes, video practice & diet log</p>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-[#999] font-medium">
          <span>Less</span>
          {[
            { bg: T.lightGray, label: "No Activity" },
            { bg: T.slateGray, label: "Minimal" },
            { bg: `linear-gradient(135deg,${T.pinkLight},${T.pinkLight2})`, label: "Moderate" },
            { bg: `linear-gradient(135deg,${T.olive},${T.olive2})`, label: "Good" },
            { bg: `linear-gradient(135deg,${T.orange},${T.orange2})`, label: "High" }
          ].map((item, i) => (
            <div key={i} title={item.label} className="w-3.5 h-3.5 rounded-[4px]" style={{ background: item.bg }} />
          ))}
          <span>More</span>
        </div>
      </div>

      <div className="overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="flex gap-4 min-w-[720px]">
          {monthCols.map(m => (
            <div key={m.monthName} className="flex-1 flex flex-col gap-1.5">
              <span className="text-[10px] font-bold text-[#AAA] text-center uppercase tracking-wider">{m.monthName}</span>
              <div className="grid grid-cols-4 gap-1">
                {m.days.map(d => {
                  const cellDate = new Date(year, m.monthIndex, d);
                  const cellKey = getLocalDateKey(cellDate);
                  const isFuture = cellDate > today;
                  const isToday = cellKey === todayKey;
                  const act = activityByDate[cellKey] || { liveCount: 0, videoCount: 0, checkedIn: false, missedCount: 0 };
                  const isDietDone = Boolean(dietCompletedState[cellKey]);

                  // Combined activity score across all features
                  const totalScore = (act.checkedIn ? 1 : 0) + (act.liveCount || 0) + (act.videoCount || 0) + (isDietDone ? 1 : 0);

                  let bg = T.lightGray; // 0 Activity (Light Gray)
                  if (isFuture) bg = "rgba(239,229,216,0.15)";
                  else if (totalScore >= 4 || isToday) bg = `linear-gradient(135deg,${T.orange},${T.orange2})`; // High Activity (Orange)
                  else if (totalScore === 3) bg = `linear-gradient(135deg,${T.olive},${T.olive2})`; // Good Activity (Olive Green)
                  else if (totalScore === 2) bg = `linear-gradient(135deg,${T.pinkLight},${T.pinkLight2})`; // Moderate Activity (Light Pink)
                  else if (totalScore === 1) bg = T.slateGray; // Minimal Activity (Gray)
                  else if (act.missedCount > 0) bg = "rgba(226,75,74,0.25)";

                  return (
                    <motion.div
                      key={d}
                      whileHover={{ scale: 1.4, zIndex: 10 }}
                      onMouseEnter={() => onHoverDay && onHoverDay(d, cellDate, act)}
                      onMouseLeave={() => onHoverDay && onHoverDay(null, null, null)}
                      className="w-3.5 h-3.5 rounded-[3.5px] cursor-pointer"
                      style={{ background: bg }}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── MAIN ATTENDANCE PAGE ────────────────────────────────────────────────────
export default function AttendancePage() {
  const checkInCtx = useCheckIn() || {};
  const stats = checkInCtx.stats || { streakDays: 0, bestStreak: 0, totalPractices: 0, wellnessScore: 0 };
  const checkedIn = checkInCtx.checkedIn || false;

  const now = useMemo(() => new Date(), []);
  const currentYear = now.getFullYear();
  const currentMonthIdx = now.getMonth();

  const [curMonth, setCurMonth] = useState(currentMonthIdx);
  const [selectedDay, setSelectedDay] = useState(now.getDate());
  const [historyMonthFilter, setHistoryMonthFilter] = useState("all");
  const [historyPage, setHistoryPage] = useState(1);

  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [liveSessions, setLiveSessions] = useState([]);
  const [mindfulnessTotal, setMindfulnessTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // Tooltip / Hover state
  const [hoverData, setHoverData] = useState(null);

  // Selected Day Index for "This Week" section (0 to 6)
  const [selectedWeekIdx, setSelectedWeekIdx] = useState(() => {
    const day = now.getDay();
    return day === 0 ? 6 : day - 1; // 0=Mon, 6=Sun
  });

  // Local state for Diet Completion toggles per date key
  // Local state for Diet Completion toggles per date key (populated from database)
  const [dietCompletedState, setDietCompletedState] = useState({});

  // Fetch real data on mount & checkIn change
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const [attRes, sessRes, mindRes, dietRes] = await Promise.allSettled([
          userAPI.getAttendanceHistory(),
          liveSessionAPI.getSessions(),
          userAPI.getMindfulness(),
          dietPlanAPI.getMyPlan()
        ]);

        if (attRes.status === "fulfilled" && attRes.value.data?.success) {
          const rawData = attRes.value.data.data;
          setAttendanceHistory(Array.isArray(rawData) ? rawData : []);
        } else {
          setAttendanceHistory([]);
        }

        if (sessRes.status === "fulfilled" && sessRes.value.data?.success) {
          const rawData = sessRes.value.data.data;
          setLiveSessions(Array.isArray(rawData) ? rawData : []);
        } else {
          setLiveSessions([]);
        }

        if (mindRes.status === "fulfilled" && mindRes.value.data?.success) {
          setMindfulnessTotal(mindRes.value.data.totalMinutes || 0);
        }

        if (dietRes.status === "fulfilled" && dietRes.value.data) {
          const completedMeals = dietRes.value.data.completedMeals || dietRes.value.data.CompletedMeals || [];
          const localMap = {};
          completedMeals.forEach(log => {
            if (log.completedAt) {
              const k = getLocalDateKey(log.completedAt);
              if (k) localMap[k] = true;
            }
          });
          setDietCompletedState(localMap);
        }
      } catch (e) {
        console.error("Failed to load attendance page data:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchAllData();
  }, [checkedIn]);

  // Toast notification for uncompleted diet plan today
  useEffect(() => {
    const todayKey = getLocalDateKey(now);
    if (!dietCompletedState[todayKey]) {
      toast.info("Reminder: Complete your Daily Diet & Yoga Practice for today!", { toastId: "diet-reminder-toast" });
    }
  }, [dietCompletedState, now]);

  // Aggregate activity by Date Key (YYYY-MM-DD)
  const activityByDate = useMemo(() => {
    const map = {};

    const safeAtt = Array.isArray(attendanceHistory) ? attendanceHistory : [];
    safeAtt.forEach(att => {
      if (!att || !att.attendanceDate) return;
      const k = getLocalDateKey(att.attendanceDate);
      if (!k) return;
      if (!map[k]) map[k] = { liveCount: 0, videoCount: 0, checkedIn: true, missedCount: 0, items: [] };
      map[k].checkedIn = true;
      map[k].items.push({
        type: "Check-in",
        title: "Daily Yoga Practice Check-in",
        time: att.checkInTime || "Logged",
        location: att.location || "Online Studio"
      });
    });

    const safeSess = Array.isArray(liveSessions) ? liveSessions : [];
    const today = new Date();
    safeSess.forEach(sess => {
      if (!sess || !sess.scheduledAt) return;
      const k = getLocalDateKey(sess.scheduledAt);
      if (!k) return;
      if (!map[k]) map[k] = { liveCount: 0, videoCount: 0, checkedIn: false, missedCount: 0, items: [] };

      const sessDate = new Date(sess.scheduledAt);
      if (sess.isCheckedIn) {
        map[k].liveCount++;
        map[k].items.push({
          type: "Live Session",
          title: sess.title || "Live Session",
          time: new Date(sess.scheduledAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          instr: sess.instructorName || "Instructor",
          status: "Attended"
        });
      } else if (sessDate < today && !sess.isCheckedIn) {
        map[k].missedCount++;
        map[k].items.push({
          type: "Live Session",
          title: sess.title || "Live Session",
          time: new Date(sess.scheduledAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          status: "Missed"
        });
      }
    });

    return map;
  }, [attendanceHistory, liveSessions]);

  // Total Attended Live Sessions Count (100% matched with Dashboard completedCount)
  const totalAttendedSessionsCount = useMemo(() => {
    const safeSess = Array.isArray(liveSessions) ? liveSessions : [];
    const liveCheckedIn = safeSess.filter(s => s && s.isCheckedIn).length;
    return liveCheckedIn;
  }, [liveSessions]);

  // Monthly stats calculation for dual ring
  const monthStats = useMemo(() => {
    const year = currentYear;
    const month = curMonth;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    let attendedDaysCount = 0;

    for (let d = 1; d <= daysInMonth; d++) {
      const k = getLocalDateKey(new Date(year, month, d));
      const act = activityByDate[k];
      if (act && (act.liveCount > 0 || act.videoCount > 0 || act.checkedIn)) {
        attendedDaysCount++;
      }
    }

    const todayDayNum = now.getDate();
    const pastDaysInMonth = curMonth === currentMonthIdx ? todayDayNum : daysInMonth;
    const pct = Math.min(100, Math.round((attendedDaysCount / (pastDaysInMonth || 1)) * 100));

    return {
      attendedDaysCount,
      totalDays: daysInMonth,
      pct
    };
  }, [activityByDate, curMonth, currentYear, currentMonthIdx, now]);

  // Dual Ring SVG constants
  const R1 = 66, C1 = 2 * Math.PI * R1, O1 = C1 - (monthStats.pct / 100) * C1;
  const R2 = 48, C2 = 2 * Math.PI * R2, O2 = C2 - (monthStats.pct / 100) * C2;

  // KPI Calculations
  const totalHoursPracticed = useMemo(() => {
    const videoMins = mindfulnessTotal || 0;
    const safeSess = Array.isArray(liveSessions) ? liveSessions : [];
    const liveMins = safeSess.filter(s => s && s.isCheckedIn).reduce((acc, s) => acc + (s.durationInMinutes || 60), 0);
    const totalMins = videoMins + liveMins;
    const totalHoursVal = Math.round((totalMins / 60) * 10) / 10;
    const liveHoursVal = Math.round((liveMins / 60) * 10) / 10;
    const videoHoursVal = Math.round((videoMins / 60) * 10) / 10;
    return {
      formatted: `${totalHoursVal}h`,
      liveHours: `${liveHoursVal}h`,
      videoHours: `${videoHoursVal}h`,
      breakdown: `${liveHoursVal}h Live · ${videoHoursVal}h Video`
    };
  }, [mindfulnessTotal, liveSessions]);

  const longestStreakDays = useMemo(() => {
    const safeAtt = Array.isArray(attendanceHistory) ? attendanceHistory : [];
    if (safeAtt.length === 0) return stats?.streakDays || 0;
    const sortedDates = Array.from(new Set(
      safeAtt.map(a => a && getLocalDateKey(a.attendanceDate)).filter(Boolean)
    )).sort((a, b) => new Date(a) - new Date(b));

    let maxStreak = 0, current = 0, prev = null;
    for (let dStr of sortedDates) {
      const curr = new Date(dStr);
      if (!prev) {
        current = 1;
      } else {
        const diffDays = Math.round((curr - prev) / (1000 * 60 * 60 * 24));
        if (diffDays === 1) current++;
        else {
          if (current > maxStreak) maxStreak = current;
          current = 1;
        }
      }
      prev = curr;
    }
    return Math.max(maxStreak, current, stats?.streakDays || 0);
  }, [attendanceHistory, stats]);

  // This Week Days Breakdown (Mon – Sun)
  const weekData = useMemo(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const mondayDiff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(today);
    monday.setDate(today.getDate() + mondayDiff);
    monday.setHours(0,0,0,0);

    const weekDaysArr = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      const k = getLocalDateKey(d);
      const act = activityByDate[k] || {};
      const done = Boolean(act.liveCount > 0 || act.videoCount > 0 || act.checkedIn);
      const dayIdx = d.getDay();

      weekDaysArr.push({
        index: i,
        dayName: DAYS_SHORT[d.getDay()],
        dateStr: k,
        dateObj: d,
        done,
        isToday: k === getLocalDateKey(today),
        dietTask: DIET_TASKS_BY_DAY[dayIdx] || "Ayurvedic Balanced Diet & Warm Water"
      });
    }

    return weekDaysArr;
  }, [activityByDate]);

  // Selected Day Task object for "This Week" card
  const selectedWeekDayObj = weekData[selectedWeekIdx] || weekData[0];
  const isSelectedDietDone = Boolean(dietCompletedState[selectedWeekDayObj?.dateStr]);

  const toggleDietCompleted = async (dateStr) => {
    if (!dateStr) return;
    const isCurrentlyDone = Boolean(dietCompletedState[dateStr]);
    const nextStatus = !isCurrentlyDone;

    // Optimistic state update
    setDietCompletedState(prev => ({ ...prev, [dateStr]: nextStatus }));

    try {
      // Map dateStr (YYYY-MM-DD) to day of week (e.g. "Monday")
      const dateObj = new Date(dateStr);
      const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
      const dayName = days[dateObj.getDay()];

      await dietPlanAPI.toggleMealLog({
        day: dayName,
        mealName: "Daily Diet Tracker Completed",
        isCompleted: nextStatus
      });
      toast.success(nextStatus ? "Daily Diet Completed! ✦" : "Daily Diet Marked Incomplete");
    } catch (err) {
      console.error("Failed to sync diet completion:", err);
      toast.error("Failed to sync status.");
      // Rollback
      setDietCompletedState(prev => ({ ...prev, [dateStr]: isCurrentlyDone }));
    }
  };

  // Today's Activity Item
  const todayActivity = useMemo(() => {
    const todayKey = getLocalDateKey(now);
    const act = activityByDate[todayKey] || { items: [] };
    const safeSess = Array.isArray(liveSessions) ? liveSessions : [];
    const upcoming = safeSess.filter(s => {
      if (!s || !s.scheduledAt) return false;
      const sk = getLocalDateKey(s.scheduledAt);
      return sk === todayKey && !s.isCheckedIn && new Date(s.scheduledAt) > now;
    });

    const items = Array.isArray(act.items) ? act.items : [];
    return {
      attendedItems: items.filter(i => i && i.status !== "Missed"),
      upcomingItems: upcoming
    };
  }, [activityByDate, liveSessions, now]);

  // Session History List with Month Filter & Pagination
  const historyList = useMemo(() => {
    const list = [];
    const safeAtt = Array.isArray(attendanceHistory) ? attendanceHistory : [];
    safeAtt.forEach(att => {
      if (!att || !att.attendanceDate) return;
      const d = new Date(att.attendanceDate);
      list.push({
        id: `att-${att.id}`,
        dateObj: d,
        dateStr: d.toLocaleDateString("en-IN", { day: 'numeric', month: 'short' }),
        title: "Daily Check-in Practice",
        cat: "Asana",
        dur: "45 min",
        time: att.checkInTime || "Logged",
        instr: "Self Practice",
        status: att.status || "Present"
      });
    });

    const safeSess = Array.isArray(liveSessions) ? liveSessions : [];
    safeSess.forEach(sess => {
      if (sess && sess.isCheckedIn && sess.scheduledAt) {
        const d = new Date(sess.scheduledAt);
        list.push({
          id: `sess-${sess.id}`,
          dateObj: d,
          dateStr: d.toLocaleDateString("en-IN", { day: 'numeric', month: 'short' }),
          title: sess.title || "Live Session",
          cat: sess.category || "Asana",
          dur: `${sess.durationInMinutes || 45} min`,
          time: new Date(sess.scheduledAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          instr: sess.instructorName || "Instructor",
          status: "Attended"
        });
      }
    });

    const sorted = list.sort((a, b) => b.dateObj - a.dateObj);

    if (historyMonthFilter === "all") return sorted;
    const monthIndex = parseInt(historyMonthFilter);
    return sorted.filter(item => item.dateObj && item.dateObj.getMonth() === monthIndex);
  }, [attendanceHistory, liveSessions, historyMonthFilter]);

  // Reset pagination to page 1 on filter change
  useEffect(() => {
    setHistoryPage(1);
  }, [historyMonthFilter]);

  const ITEMS_PER_PAGE = 10;
  const totalHistoryPages = Math.max(1, Math.ceil(historyList.length / ITEMS_PER_PAGE));
  const paginatedHistory = useMemo(() => {
    return historyList.slice((historyPage - 1) * ITEMS_PER_PAGE, historyPage * ITEMS_PER_PAGE);
  }, [historyList, historyPage]);

  // Monthly Performance Bars (Jan - Dec)
  const monthlyPerformance = useMemo(() => {
    const monthCounts = Array(12).fill(0);
    const safeAtt = Array.isArray(attendanceHistory) ? attendanceHistory : [];
    safeAtt.forEach(att => {
      if (att && att.attendanceDate) {
        const m = new Date(att.attendanceDate).getMonth();
        if (!isNaN(m) && m >= 0 && m < 12) monthCounts[m]++;
      }
    });

    const safeSess = Array.isArray(liveSessions) ? liveSessions : [];
    safeSess.forEach(s => {
      if (s && s.isCheckedIn && s.scheduledAt) {
        const m = new Date(s.scheduledAt).getMonth();
        if (!isNaN(m) && m >= 0 && m < 12) monthCounts[m]++;
      }
    });
    return monthCounts;
  }, [attendanceHistory, liveSessions]);

  const maxMonthlyVal = Math.max(...monthlyPerformance, 1);

  if (loading) {
    return (
      <UserDashboardLayout>
        <BrandLoader 
          message="Loading Attendance Statistics..." 
          subMessage="Syncing your practice calendar and attendance records" 
        />
      </UserDashboardLayout>
    );
  }

  const todayKeyStr = getLocalDateKey(now);
  const isTodayDietDone = Boolean(dietCompletedState[todayKeyStr]);

  return (
    <UserDashboardLayout>
      <div style={{ fontFamily: "'Inter',sans-serif" }}>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@700;800;900&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />

        {/* Ambient Orbs */}
        <motion.div animate={{ y: [0,-18,0], x: [0,12,0] }} transition={{ duration: 13, repeat: Infinity, ease: "easeInOut" }}
          className="fixed w-[440px] h-[440px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle,rgba(242,154,12,0.08),transparent 70%)", filter: "blur(80px)", top: -140, right: -90 }} />
        <motion.div animate={{ y: [0,14,0], x: [0,-10,0] }} transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
          className="fixed w-[360px] h-[360px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle,rgba(107,138,90,0.07),transparent 70%)", filter: "blur(80px)", bottom: -80, left: -80 }} />

        <div className="relative z-10 w-full min-w-0">
          <Topbar />

          {/* 🔔 Daily Diet & Practice Reminder Banner */}
          {!isTodayDietDone && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-5 rounded-[22px] p-4 flex items-center justify-between gap-4 border flex-wrap"
              style={{
                background: "linear-gradient(135deg,rgba(255,244,232,0.95),rgba(255,238,218,0.95))",
                borderColor: "rgba(242,154,12,0.3)",
                boxShadow: "0 8px 24px rgba(242,154,12,0.12)"
              }}
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[#F29A0C] text-white flex items-center justify-center flex-shrink-0 shadow-md">
                  <FiBell size={16} />
                </div>
                <div>
                  <p className="text-[13px] font-bold text-[#1F1F1F]">
                    Reminder: Complete your Daily Diet & Yoga Practice!
                  </p>
                  <p className="text-[11px] text-[#888]">
                    {DIET_TASKS_BY_DAY[now.getDay()]}
                  </p>
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                onClick={() => toggleDietCompleted(todayKeyStr)}
                className="px-4 py-2 rounded-full text-[12px] font-bold text-white cursor-pointer shadow-md flex-shrink-0"
                style={{ background: `linear-gradient(135deg,${T.orange},${T.orange2})` }}
              >
                Mark Completed ✦
              </motion.button>
            </motion.div>
          )}

          {/* ── HERO BANNER ── */}
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="relative overflow-hidden rounded-[36px] p-6 sm:p-8 md:p-10 mb-6" style={glass}>
            <div className="absolute inset-0 pointer-events-none opacity-[0.03] rounded-[inherit]"
              style={{ backgroundImage: "radial-gradient(circle,#000 1px,transparent 0)", backgroundSize: "22px 22px" }} />
            <div className="absolute top-0 right-0 pointer-events-none"
              style={{ width: 280, height: 280, background: "radial-gradient(circle,rgba(242,154,12,0.09),transparent)", filter: "blur(40px)" }} />

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_220px] gap-6 lg:gap-8 items-center relative">
              <div>
                <div className="flex items-center gap-3 mb-5 flex-wrap">
                  <motion.div animate={{ boxShadow: ["0 0 0 0 rgba(242,154,12,0)", "0 0 0 8px rgba(242,154,12,0.14)", "0 0 0 0 rgba(242,154,12,0)"] }}
                    transition={{ duration: 3, repeat: Infinity }}
                    className="w-11 h-11 rounded-[16px] flex items-center justify-center"
                    style={{ background: `linear-gradient(135deg,${T.orange},${T.orange2})`, boxShadow: `0 8px 22px rgba(242,154,12,0.42)` }}>
                    <FiCalendar size={20} style={{ color: "#fff" }} />
                  </motion.div>
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[1.5px]" style={{ color: T.orange }}>Attendance Dashboard</p>
                    <p className="text-[12px] text-[#999]">{MONTHS[curMonth]} {currentYear} · Practice Records</p>
                  </div>
                  <div className="ml-auto sm:ml-3 flex items-center gap-2 px-4 py-2 rounded-full text-[11px] font-bold flex-shrink-0"
                    style={{ background: "rgba(107,138,90,0.1)", border: "1px solid rgba(107,138,90,0.2)", color: "#27500A" }}>
                    <motion.span animate={{ scaleY: [1,1.1,1], rotate: [-1,1,-1] }} transition={{ duration: 1.5, repeat: Infinity }}>
                      <FiFeather size={11} />
                    </motion.span>
                    {stats?.streakDays || 0}-Day Active Streak
                  </div>
                </div>
                <h1 className="font-black mb-3 text-[#1F1F1F] text-[26px] sm:text-[32px] lg:text-[36px]"
                  style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-1.5px", lineHeight: 1.06 }}>
                  Show up, every day.{" "}
                  <span style={{ background: `linear-gradient(135deg,${T.orange},${T.orange2})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                    Progress follows.
                  </span>
                </h1>
                <p className="text-[14px] leading-[1.8] text-[#777]" style={{ maxWidth: 500 }}>
                  Track every live session and practice video watched. Consistency shapes your physical and mental transformation on the mat.
                </p>
              </div>

              {/* Dual Progress Ring */}
              <div className="text-center flex-shrink-0">
                <div style={{ position: "relative", width: 160, height: 160, margin: "0 auto" }}>
                  <svg viewBox="0 0 160 160" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", transform: "rotate(-90deg)" }}>
                    <circle cx="80" cy="80" r={R1} stroke="#F1E8DA" strokeWidth="10" fill="none" />
                    <motion.circle cx="80" cy="80" r={R1} stroke="url(#ahg1)" strokeWidth="10" fill="none" strokeLinecap="round"
                      strokeDasharray={C1} initial={{ strokeDashoffset: C1 }} animate={{ strokeDashoffset: O1 }}
                      transition={{ duration: 1.6, delay: 0.2, ease: "easeOut" }} />
                    <circle cx="80" cy="80" r={R2} stroke="#ECF5E8" strokeWidth="8" fill="none" />
                    <motion.circle cx="80" cy="80" r={R2} stroke="url(#ahg2)" strokeWidth="8" fill="none" strokeLinecap="round"
                      strokeDasharray={C2} initial={{ strokeDashoffset: C2 }} animate={{ strokeDashoffset: O2 }}
                      transition={{ duration: 1.6, delay: 0.4, ease: "easeOut" }} />
                    <defs>
                      <linearGradient id="ahg1" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stopColor={T.orange}/><stop offset="100%" stopColor={T.orange2}/></linearGradient>
                      <linearGradient id="ahg2" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stopColor={T.olive}/><stop offset="100%" stopColor={T.olive2}/></linearGradient>
                    </defs>
                  </svg>
                  <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                    <p className="font-black text-[#1F1F1F] leading-none" style={{ fontFamily: "'Poppins',sans-serif", fontSize: 34, letterSpacing: "-1.5px" }}>
                      {monthStats.pct}%
                    </p>
                    <p className="text-[11px] mt-1 text-[#999]">This month</p>
                  </div>
                </div>
                <p className="font-black text-[14px] text-[#1F1F1F] mt-3" style={{ fontFamily: "'Poppins',sans-serif" }}>
                  {totalAttendedSessionsCount} / {monthStats.totalDays} sessions
                </p>
                <p className="text-[11px] mt-0.5 text-[#999]">Sessions attended</p>
              </div>
            </div>
          </motion.div>

          {/* ── KPI STATS BAND ── */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="ud-kpi grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 lg:gap-0 mb-6 rounded-[24px] overflow-hidden"
            style={{ border: "1px solid rgba(255,255,255,0.9)", boxShadow: "0 12px 40px rgba(0,0,0,0.05)" }}>
            {[
              { icon: FiFeather,       val: `${stats?.streakDays || 0}`,              label: "Current Streak",      sub: "Active streak",        accent: T.orange, bg: "linear-gradient(145deg,#FFF4E8,#FFF8EE)" },
              { icon: FiAward,         val: `${longestStreakDays}`,                  label: "Longest Streak",      sub: "Personal best",        accent: T.olive,  bg: "linear-gradient(145deg,#F5FAF2,#F9FDF5)" },
              { icon: FiCheckCircle,   val: `${totalAttendedSessionsCount}`,         label: "Total Sessions",      sub: "Live classes",         accent: T.orange, bg: "linear-gradient(145deg,#FFF4E8,#FFF8EE)" },
              { icon: FiClock,         val: totalHoursPracticed.formatted,           label: "Total Practice Hours", sub: totalHoursPracticed.breakdown, accent: T.olive,  bg: "linear-gradient(145deg,#F5FAF2,#F9FDF5)" },
              { icon: FiTrendingUp,    val: `${monthStats.pct}%`,                    label: "Adherence Rate",      sub: "This month's goal",    accent: T.orange, bg: "linear-gradient(145deg,#FFF4E8,#FFF8EE)" },
            ].map((s, i) => (
              <motion.div key={s.label} whileHover={{ background: "rgba(255,255,255,0.95)" }}
                className="relative overflow-hidden px-5 py-5 flex items-center gap-3"
                style={{ background: s.bg, borderRight: i < 4 ? `1px solid rgba(239,229,216,0.5)` : "none" }}>
                <div className="absolute bottom-0 left-0 right-0 h-[2px]" style={{ background: `linear-gradient(90deg,${s.accent},${s.accent}88)` }} />
                <div className="w-9 h-9 rounded-[12px] flex items-center justify-center flex-shrink-0"
                  style={{ background: `linear-gradient(135deg,${s.accent},${s.accent}CC)`, boxShadow: `0 6px 16px ${s.accent}40` }}>
                  <s.icon size={16} style={{ color: "#fff" }} />
                </div>
                <div className="min-w-0">
                  <p className="ud-kpi-val font-black text-[#1F1F1F]" style={{ fontFamily: "'Poppins',sans-serif", fontSize: 24, letterSpacing: "-1px", lineHeight: 1 }}>{s.val}</p>
                  <p className="ud-kpi-lbl text-[10px] font-bold uppercase tracking-[0.7px] mt-0.5 text-[#AAA] truncate">{s.label}</p>
                  {s.sub && (
                    <p className="ud-kpi-sub text-[9.5px] font-semibold text-[#888] mt-0.5 truncate" title={s.sub}>
                      {s.sub}
                    </p>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* ── MAIN GRID (MONTH CALENDAR + STREAK & TASK CARDS) ── */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.14 }}
            className="grid grid-cols-1 lg:grid-cols-[1fr_320px] xl:grid-cols-[1fr_360px] gap-5 mb-6">

            <div className="relative">
              <MonthCalendar
                year={currentYear}
                month={curMonth}
                selectedDay={selectedDay}
                onSelect={setSelectedDay}
                onPrev={() => curMonth > 0 && setCurMonth(curMonth - 1)}
                onNext={() => curMonth < 11 && setCurMonth(curMonth + 1)}
                activityByDate={activityByDate}
                onHoverDay={(d, dateObj, act) => {
                  if (d && dateObj) setHoverData({ day: d, dateObj, act });
                  else setHoverData(null);
                }}
              />

              {/* Hover Day Info Popover */}
              <AnimatePresence>
                {hoverData && (
                  <motion.div
                    initial={{ opacity: 0, y: 8, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 5 }}
                    className="absolute top-4 right-4 z-20 w-[260px] rounded-[20px] p-4 bg-white/95 border border-[#F29A0C]/20 shadow-xl backdrop-blur-md"
                  >
                    <p className="text-[12px] font-black text-[#1F1F1F]" style={{ fontFamily: "'Poppins',sans-serif" }}>
                      {hoverData.dateObj.toLocaleDateString("en-IN", { weekday: 'short', month: 'short', day: 'numeric' })}
                    </p>
                    <div className="mt-2 text-[11px] flex flex-col gap-1.5">
                      {(!hoverData.act?.items || hoverData.act.items.length === 0) ? (
                        <p className="text-[#AAA] italic">Rest day — No sessions recorded</p>
                      ) : (
                        hoverData.act.items.map((it, idx) => (
                          <div key={idx} className="flex items-center gap-2 p-2 rounded-lg bg-[#FAF6F0]">
                            <FiCheckCircle size={12} className="text-[#F29A0C] flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                              <p className="font-bold text-[#1F1F1F] truncate">{it.title}</p>
                              <p className="text-[9px] text-[#888]">{it.time} {it.instr ? `· ${it.instr}` : ""}</p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Right Column Cards */}
            <div className="flex flex-col gap-5">
              {/* Current Streak Dark Card */}
              <div className="relative overflow-hidden rounded-[28px] p-6"
                style={{ background: "linear-gradient(145deg,#1F1F1F,#2D2D2D)", boxShadow: "0 20px 55px rgba(0,0,0,0.32)" }}>
                <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
                  style={{ backgroundImage: "radial-gradient(circle,#fff 1px,transparent 0)", backgroundSize: "14px 14px" }} />
                <div className="relative">
                  <p className="text-[10px] font-bold uppercase tracking-[1.5px] mb-4 text-white/45">Current Streak</p>
                  <div className="flex items-center gap-3 mb-5">
                    <p className="font-black text-white leading-none" style={{ fontFamily: "'Poppins',sans-serif", fontSize: 52, letterSpacing: "-2px" }}>
                      {stats?.streakDays || 0}
                    </p>
                    <div>
                      <p className="font-bold text-[16px] text-white/70">days</p>
                      <div className="flex items-center gap-1.5 mt-1">
                        <motion.div animate={{ scaleY: [1,1.1,1], rotate: [-1,1,-1] }} transition={{ duration: 1.5, repeat: Infinity }}>
                          <FiFeather size={16} style={{ color: T.orange }} />
                        </motion.div>
                        <span className="text-[12px] font-bold" style={{ color: T.orange }}>
                          {(stats?.streakDays || 0) > 0 ? "On fire!" : "Start today!"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-[14px] p-3.5 mb-4" style={{ background: "rgba(255,255,255,0.08)" }}>
                    <div className="flex justify-between text-[11px] mb-2">
                      <span className="text-white/55">Next milestone</span>
                      <span className="font-bold" style={{ color: T.orange }}>{stats?.streakDays || 0} / 21 days</span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.1)" }}>
                      <div className="h-full rounded-full" style={{ width: `${Math.min(100, Math.round(((stats?.streakDays || 0) / 21) * 100))}%`, background: `linear-gradient(90deg,${T.orange},${T.orange2})` }} />
                    </div>
                    <p className="text-[10px] mt-1.5 text-white/40">
                      {Math.max(0, 21 - (stats?.streakDays || 0))} more days to unlock 21-Day badge
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    {[
                      [longestStreakDays.toString(), "Longest Streak"],
                      [`${totalAttendedSessionsCount}d`, "Total Attended"]
                    ].map(([v, l]) => (
                      <div key={l} className="rounded-[12px] p-2.5" style={{ background: "rgba(255,255,255,0.07)" }}>
                        <p className="font-black text-white text-[18px]" style={{ fontFamily: "'Poppins',sans-serif" }}>{v}</p>
                        <p className="text-[9px] font-bold uppercase tracking-[0.7px] mt-1 text-white/40">{l}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Interactive "This Week" Task & Diet Section (Standard Icons) */}
              <div className="rounded-[28px] p-5" style={glass}>
                <div className="flex justify-between items-center mb-1">
                  <p className="font-black text-[15px] text-[#1F1F1F]" style={{ fontFamily: "'Poppins',sans-serif" }}>This Week Tasks</p>
                  <span className="text-[10px] font-bold text-[#F29A0C] bg-[#F29A0C]/10 px-2.5 py-0.5 rounded-full">Interactive</span>
                </div>
                <p className="text-[11px] mb-3 text-[#AAA]">Click any day to view & check off tasks</p>
                
                <div className="flex gap-1 mb-4 justify-between">
                  {weekData.map(w => (
                    <motion.button
                      key={w.dateStr}
                      whileHover={{ scale: 1.08 }}
                      whileTap={{ scale: 0.94 }}
                      onClick={() => setSelectedWeekIdx(w.index)}
                      className="flex flex-col items-center gap-1.5 cursor-pointer border-none bg-transparent"
                    >
                      <div className="w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200"
                        style={{
                          background: selectedWeekIdx === w.index
                            ? `linear-gradient(135deg,${T.orange},${T.orange2})`
                            : w.done
                            ? `linear-gradient(135deg,${T.olive},${T.olive2})`
                            : "rgba(239,229,216,0.5)",
                          boxShadow: selectedWeekIdx === w.index
                            ? `0 4px 12px rgba(242,154,12,0.4)`
                            : w.done
                            ? `0 3px 8px rgba(107,138,90,0.3)`
                            : "none",
                          border: selectedWeekIdx === w.index ? "2px solid #fff" : "none"
                        }}>
                        {(w.done || w.isToday) && <FiCheckCircle size={13} style={{ color: "#fff" }} />}
                      </div>
                      <span className="text-[9px] font-bold" style={{ color: selectedWeekIdx === w.index ? T.orange : T.muted }}>
                        {w.dayName}
                      </span>
                    </motion.button>
                  ))}
                </div>

                {/* Task Checklist with Standard React-Icons */}
                <div className="rounded-[18px] p-3.5 bg-[#FAF6F0] border border-[#EFE5D8]/80 flex flex-col gap-2.5">
                  <div className="flex justify-between items-center pb-2 border-b border-[#EFE5D8]">
                    <span className="text-[11px] font-bold text-[#1F1F1F]">
                      {selectedWeekDayObj?.dayName} Tasks ({selectedWeekDayObj?.dateStr})
                    </span>
                    {selectedWeekDayObj?.isToday && (
                      <span className="text-[9px] font-extrabold text-white bg-[#E97818] px-2 py-0.5 rounded-full">TODAY</span>
                    )}
                  </div>

                  {/* 1. Diet Plan Meal Task (Standard Icon FiActivity) */}
                  <div
                    onClick={() => toggleDietCompleted(selectedWeekDayObj?.dateStr)}
                    className="flex items-center gap-2.5 p-2 rounded-[12px] cursor-pointer transition-colors"
                    style={{ background: isSelectedDietDone ? "rgba(107,138,90,0.12)" : "rgba(255,255,255,0.9)" }}
                  >
                    <div className="w-5 h-5 rounded-full flex items-center justify-center border transition-all"
                      style={{
                        background: isSelectedDietDone ? T.olive : "transparent",
                        borderColor: isSelectedDietDone ? T.olive : "#CCC"
                      }}>
                      {isSelectedDietDone ? <FiCheck size={12} className="text-white" /> : <FiActivity size={10} className="text-[#888]" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-[11px] font-bold ${isSelectedDietDone ? 'line-through text-[#888]' : 'text-[#1F1F1F]'}`}>
                        {selectedWeekDayObj?.dietTask}
                      </p>
                      <p className="text-[9px] text-[#AAA]">{isSelectedDietDone ? "Diet Goal Completed ✦" : "Click to mark meal plan done"}</p>
                    </div>
                  </div>

                  {/* 2. Practice Check-in Task (Standard Icon FiSunrise) */}
                  <div className="flex items-center gap-2.5 p-2 rounded-[12px] bg-white/90">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center border"
                      style={{
                        background: selectedWeekDayObj?.done ? T.orange : "transparent",
                        borderColor: selectedWeekDayObj?.done ? T.orange : "#CCC"
                      }}>
                      {selectedWeekDayObj?.done ? <FiCheck size={12} className="text-white" /> : <FiSunrise size={10} className="text-[#888]" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-[11px] font-bold ${selectedWeekDayObj?.done ? 'text-[#1F1F1F]' : 'text-[#888]'}`}>
                        Yoga Practice & Check-in
                      </p>
                      <p className="text-[9px] text-[#AAA]">{selectedWeekDayObj?.done ? "Logged & Verified" : "Pending Check-in"}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Today Section */}
              <div className="rounded-[28px] p-5" style={glass}>
                <p className="font-black text-[15px] text-[#1F1F1F] mb-1" style={{ fontFamily: "'Poppins',sans-serif" }}>Today</p>
                <p className="text-[11px] mb-4 text-[#AAA]">
                  {now.toLocaleDateString("en-US", { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })}
                </p>
                <div className="flex flex-col gap-2">
                  {todayActivity.attendedItems.map((item, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-[14px]" style={{ background: "rgba(245,250,242,0.8)", border: "1px solid rgba(107,138,90,0.2)" }}>
                      <div className="w-8 h-8 rounded-[10px] flex items-center justify-center flex-shrink-0" style={{ background: `linear-gradient(135deg,${T.olive},${T.olive2})` }}>
                        <FiSunrise size={14} style={{ color: "#fff" }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[12px] font-bold text-[#1F1F1F] truncate">{item.title}</p>
                        <p className="text-[10px] text-[#888] truncate">{item.time} {item.instr ? `· ${item.instr}` : ""}</p>
                      </div>
                      <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: `linear-gradient(135deg,${T.olive},${T.olive2})` }}>
                        <FiCheck size={11} style={{ color: "#fff" }} />
                      </div>
                    </div>
                  ))}

                  {todayActivity.upcomingItems.map((sess) => (
                    <div key={sess.id} className="flex items-center gap-3 p-3 rounded-[14px]" style={{ background: "rgba(255,244,232,0.8)", border: "1px solid rgba(242,154,12,0.2)" }}>
                      <div className="w-8 h-8 rounded-[10px] flex items-center justify-center flex-shrink-0" style={{ background: `linear-gradient(135deg,${T.orange},${T.orange2})` }}>
                        <FiWind size={14} style={{ color: "#fff" }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[12px] font-bold text-[#1F1F1F] truncate">{sess.title}</p>
                        <p className="text-[10px] text-[#888] truncate">
                          {new Date(sess.scheduledAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · {sess.instructorName || "Maya"}
                        </p>
                      </div>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-[#E97818] bg-[#F29A0C]/10">Upcoming</span>
                    </div>
                  ))}

                  {todayActivity.attendedItems.length === 0 && todayActivity.upcomingItems.length === 0 && (
                    <div className="p-3 text-center rounded-[14px] bg-[#FAF6F0] text-[11px] text-[#AAA] italic">
                      No live sessions scheduled for today. Check in to log practice!
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>

          {/* ── YEAR HEATMAP ── */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <YearHeatmap
              year={currentYear}
              activityByDate={activityByDate}
              dietCompletedState={dietCompletedState}
              onHoverDay={(d, dateObj, act) => {
                if (d && dateObj) setHoverData({ day: d, dateObj, act });
                else setHoverData(null);
              }}
            />
          </motion.div>

          {/* ── SESSION HISTORY WITH MONTH FILTER & PAGINATION ── */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="rounded-[32px] p-7 mb-6" style={glass}>
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <div>
                <h2 className="font-black text-[20px] text-[#1F1F1F]" style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-0.5px" }}>
                  Session History ({historyList.length})
                </h2>
                <p className="text-[12px] text-[#AAA] mt-0.5">Recorded live sessions & daily check-in practices</p>
              </div>
              <div className="ud-scroll flex gap-1.5 flex-wrap">
                <motion.button
                  key="all"
                  onClick={() => setHistoryMonthFilter("all")}
                  whileTap={{ scale: 0.94 }}
                  className="px-4 py-1.5 rounded-full text-[12px] font-bold cursor-pointer transition-all"
                  style={historyMonthFilter === "all"
                    ? { background: `linear-gradient(135deg,${T.orange},${T.orange2})`, color: "#fff", boxShadow: `0 4px 12px rgba(242,154,12,0.35)` }
                    : { background: "rgba(255,255,255,0.85)", color: "#777", border: `1px solid ${T.border}` }
                  }
                >
                  All
                </motion.button>
                {MONTHS_SHORT.map((m, i) => (
                  <motion.button
                    key={m}
                    onClick={() => setHistoryMonthFilter(i.toString())}
                    whileTap={{ scale: 0.94 }}
                    className="px-3.5 py-1.5 rounded-full text-[12px] font-bold cursor-pointer transition-all"
                    style={historyMonthFilter === i.toString()
                      ? { background: `linear-gradient(135deg,${T.orange},${T.orange2})`, color: "#fff", boxShadow: `0 4px 12px rgba(242,154,12,0.35)` }
                      : { background: "rgba(255,255,255,0.85)", color: "#777", border: `1px solid ${T.border}` }
                    }
                  >
                    {m}
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Scrollable Container if > 10 items */}
            <div className="max-h-[560px] overflow-y-auto pr-1 flex flex-col gap-2.5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
              {paginatedHistory.length === 0 ? (
                <div className="p-8 text-center text-[13px] text-[#AAA] italic rounded-[20px] bg-white/50">
                  No session history recorded for this period.
                </div>
              ) : (
                paginatedHistory.map((s, i) => {
                  const Icon = CAT_ICONS[s.cat] || FiCheckCircle;
                  return (
                    <motion.div
                      key={s.id || i}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.03 }}
                      whileHover={{ x: 5, borderColor: `rgba(242,154,12,0.3)`, background: "rgba(255,255,255,0.95)" }}
                      className="flex items-center gap-4 p-4 rounded-[20px] cursor-default transition-all duration-200"
                      style={{ border: `1px solid ${T.border}`, background: "rgba(255,255,255,0.75)" }}
                    >
                      <div className="w-10 h-10 rounded-[14px] flex items-center justify-center flex-shrink-0"
                        style={{ background: CAT_BG[s.cat] || "rgba(242,154,12,0.1)", border: `1px solid ${(CAT_COLORS[s.cat] || T.orange)}30` }}>
                        <Icon size={18} style={{ color: CAT_COLORS[s.cat] || T.orange }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-[14px] text-[#1F1F1F]" style={{ fontFamily: "'Poppins',sans-serif" }}>{s.title}</p>
                        <p className="text-[11px] text-[#888] mt-0.5">{s.instr} · {s.time}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <span className="text-[11px] font-bold px-3 py-1 rounded-full" style={{ background: CAT_BG[s.cat] || "rgba(242,154,12,0.1)", color: CAT_COLORS[s.cat] || T.orange }}>
                          {s.cat}
                        </span>
                        <span className="text-[11px] text-[#AAA]">{s.dur} · {s.dateStr}</span>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </div>

            {/* Pagination Controls */}
            {historyList.length > ITEMS_PER_PAGE && (
              <div className="flex items-center justify-between mt-5 pt-4 border-t border-[#EFE5D8]">
                <p className="text-[12px] text-[#888] font-medium">
                  Showing <span className="font-bold text-[#1F1F1F]">{(historyPage - 1) * ITEMS_PER_PAGE + 1}</span> to{" "}
                  <span className="font-bold text-[#1F1F1F]">{Math.min(historyPage * ITEMS_PER_PAGE, historyList.length)}</span> of{" "}
                  <span className="font-bold text-[#1F1F1F]">{historyList.length}</span> sessions
                </p>
                <div className="flex items-center gap-2">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    disabled={historyPage === 1}
                    onClick={() => setHistoryPage(prev => Math.max(1, prev - 1))}
                    className="px-4 py-1.5 rounded-full text-[12px] font-bold border border-[#EFE5D8] bg-white cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
                  >
                    Previous
                  </motion.button>
                  <span className="text-[12px] font-bold text-[#F29A0C] px-2">
                    Page {historyPage} of {totalHistoryPages}
                  </span>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    disabled={historyPage === totalHistoryPages}
                    onClick={() => setHistoryPage(prev => Math.min(totalHistoryPages, prev + 1))}
                    className="px-4 py-1.5 rounded-full text-[12px] font-bold text-white cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
                    style={{ background: `linear-gradient(135deg,${T.orange},${T.orange2})` }}
                  >
                    Next
                  </motion.button>
                </div>
              </div>
            )}
          </motion.div>

          {/* ── MONTHLY PERFORMANCE BAR CHART ── */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="rounded-[32px] p-7 mb-8" style={glass}>
            <h2 className="font-black text-[20px] text-[#1F1F1F] mb-1" style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-0.5px" }}>
              Monthly Performance Chart ({currentYear})
            </h2>
            <p className="text-[12px] text-[#AAA] mb-6">Total sessions attended per month</p>
            <div className="flex items-end gap-3" style={{ height: 130 }}>
              {monthlyPerformance.map((v, i) => {
                const h = Math.round((v / maxMonthlyVal) * 100);
                const isCurrent = i === currentMonthIdx;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1.5 h-full justify-end">
                    <p className="text-[11px] font-black" style={{ color: isCurrent ? T.orange : T.light }}>{v}</p>
                    <motion.div
                      className="w-full rounded-t-[10px]"
                      initial={{ height: 0 }}
                      whileInView={{ height: `${Math.max(h, 6)}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: i * 0.05, ease: "easeOut" }}
                      style={{
                        background: isCurrent ? `linear-gradient(180deg,${T.orange},${T.orange2})` : "rgba(242,154,12,0.2)",
                        boxShadow: isCurrent ? `0 4px 16px rgba(242,154,12,0.42)` : "none"
                      }}
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex justify-around mt-3 border-t border-[#EFE5D8]/50 pt-2">
              {MONTHS_SHORT.map((m, i) => (
                <span key={m} className="flex-1 text-center text-[11px] font-bold" style={{ color: i === currentMonthIdx ? T.orange : T.light }}>
                  {m}
                </span>
              ))}
            </div>
          </motion.div>

        </div>
      </div>
    </UserDashboardLayout>
  );
}